import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useSelector, shallowEqual } from "react-redux";
import PropTypes from "prop-types";

// Components
import SearchDropdown from "new_components/organisms/SearchDropdown";
import Table from "new_components/organisms/Table";
import ImageButton from "new_components/molecules/ImageButton";

// Constants
import { ICON_SET } from "new_components/atoms/Icon/IconSet";
import colors from "styles/colors";
import Text from "new_components/atoms/Text";
import typography from "styles/typography";

// Providers
import { useUserSearchContext, UserSearchProvider } from "Providers/UserSearchProvider";

// Styles
const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  filter: ${({ isLoading }) => (isLoading ? "blur(4px)" : "none")};
  pointer-events: ${({ isLoading }) => (isLoading ? "none" : "auto")};
  transition: filter 0.3s ease, pointer-events 0.3s ease;
`;

const TableWrapper = styled.div`
  margin-top: 16px;
`;

const ReportAccessSection = ({ formData, onUpdate }) => {
    const { get_employees } = useSelector(
        (state) => ({
            get_employees: state.employees,
        }),
        shallowEqual
    );

    const { userList } = useUserSearchContext();

    const [selectedUsers, setSelectedUsers] = useState(
        Array.isArray(formData.collaborators) ? formData.collaborators : []
    );

    const [loadingData, setLoadingData] = useState(true);

    // Function to add a user if not already selected
    const handleAddUser = (user) => {
        if (user.value !== -1 && !selectedUsers.some((u) => u.value === user.value)) {
            const updatedUsers = [...selectedUsers, user];
            setSelectedUsers(updatedUsers);
            onUpdate(updatedUsers);
        }
    };

    useEffect(() => {
        if (get_employees?.employees?.length > 0) {
            setLoadingData(false);
        }
    }, [get_employees]);

    useEffect(() => {
        if (formData.collaborators && formData.collaborators.length > 0) {
            setSelectedUsers(formData.collaborators);
        }
    }, [formData.collaborators]);

    if (loadingData) {
        return (
            <SectionWrapper isLoading={loadingData}>
                <p>Loading data, please wait...</p>
            </SectionWrapper>
        );
    }

    // Handle removing a user
    const handleRemoveUser = (userId) => {
        const updatedUsers = selectedUsers.filter((user) => user.value !== userId);
        setSelectedUsers(updatedUsers);
        onUpdate(updatedUsers);
    };

    return (
        <SectionWrapper isLoading={loadingData}>
            <Text
                color={colors.text.gray}
                typographyStyle={typography.body.semibold}
            >
                Select users who will have access to view this survey's results and reports
            </Text>
            {/* Search Dropdown */}
            <SearchDropdown
                data={userList}
                onSearch={(item, searchTerm) =>
                    item.text.toLowerCase().includes(searchTerm.toLowerCase())
                }
                placeholder="Search for users to grant report access"
                onSelect={handleAddUser}
            />

            {/* Table of Users with Report Access */}
            <TableWrapper>
                <Table
                    columns={[
                        { key: "user", label: "User" },
                        { key: "action", label: "Action" },
                    ]}
                    data={selectedUsers.map((user) => ({
                        user: user.text,
                        action: (
                            <ImageButton
                                text="Remove"
                                icon={ICON_SET.delete}
                                textStyle
                                color={colors.auxiliary.red[500]}
                                onClick={() => handleRemoveUser(user.value)}
                            />
                        ),
                    }))}
                />

                {/* Display a message if no users are assigned */}
                {selectedUsers.length === 0 && (
                    <Text
                        color={colors.text.gray}
                        typographyStyle={typography.body.semibold}
                    >
                        No users have report access. This survey's results will only be visible to administrators and the survey creator.
                    </Text>
                )}
            </TableWrapper>
        </SectionWrapper>
    );
};

const ReportAccessSectionWrapper = ({ formData, onUpdate }) => {
    return (
        <UserSearchProvider>
            <ReportAccessSection formData={formData} onUpdate={onUpdate} />
        </UserSearchProvider>
    );
};

ReportAccessSection.propTypes = {
    formData: PropTypes.shape({
        collaborators: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                text: PropTypes.string,
                id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            })
        ),
    }),
    onUpdate: PropTypes.func.isRequired,
};

export default ReportAccessSectionWrapper;